/* Modal — accessible dialog with focus trap and Esc-to-close.
   - role="dialog" aria-modal="true"
   - aria-labelledby on a header element so screen readers announce title
   - Focus is moved into the dialog on open and trapped by Tab cycle
   - Esc closes; click on backdrop closes; focus returns to invoker on close
   - On mobile (<640px) it stretches to a full-screen sheet
*/
import React, { useEffect, useRef } from "react";
import { P } from "../../shared/constants";
import { X } from "../icons";
var E = React.createElement;

var FOCUSABLE = [
  "a[href]","button:not([disabled])","textarea:not([disabled])",
  "input:not([disabled])","select:not([disabled])","[tabindex]:not([tabindex=\"-1\"])"
].join(",");

export default function Modal(props){
  var open = !!props.open;
  var title = props.title || "Dialog";
  var labelId = props.labelId || ("modal-title-"+Math.random().toString(36).slice(2,8));
  var size = props.size || "md"; /* sm | md | lg */
  var maxW = size==="lg"?720:size==="sm"?420:560;

  var dialogRef = useRef(null);
  var invokerRef = useRef(null);
  /* Keep the latest onClose in a ref so the Escape handler can call it
     WITHOUT making onClose an effect dependency. Consumers pass a fresh
     onClose closure every render (e.g. () => setConfirmDialog(null)); if the
     effect depended on it, it would tear down + re-run on every parent
     re-render — including every keystroke in a modal input — which re-fires
     the focus-move below and yanks focus to the first focusable (the X).
     Deps are [open] only. */
  var onCloseRef = useRef(props.onClose);
  onCloseRef.current = props.onClose;

  useEffect(function(){
    if(!open)return;
    /* Remember where focus was so we can restore on close. */
    invokerRef.current = typeof document!=="undefined"?document.activeElement:null;
    /* Move focus into the dialog. Prefer the first focusable element; fall
       back to the dialog itself so screen readers announce the title. */
    var t = setTimeout(function(){
      var node = dialogRef.current;
      if(!node)return;
      /* If an autoFocus input (or anything) already placed focus inside the
         dialog, leave it — don't steal focus to the X / first focusable. */
      if(node.contains(document.activeElement)) return;
      var first = node.querySelector(FOCUSABLE);
      if(first) first.focus(); else node.focus();
    }, 0);
    function onKey(e){
      if(e.key==="Escape"){ e.stopPropagation(); onCloseRef.current && onCloseRef.current(); return; }
      if(e.key==="Tab"){
        var node = dialogRef.current; if(!node) return;
        var list = Array.prototype.slice.call(node.querySelectorAll(FOCUSABLE))
          .filter(function(el){return !el.hasAttribute("disabled")&&el.offsetParent!==null});
        if(list.length===0){ e.preventDefault(); node.focus(); return; }
        var first = list[0], last = list[list.length-1];
        var active = document.activeElement;
        if(e.shiftKey && active===first){ e.preventDefault(); last.focus(); }
        else if(!e.shiftKey && active===last){ e.preventDefault(); first.focus(); }
      }
    }
    document.addEventListener("keydown", onKey, true);
    /* Lock background scroll so users can't lose context. */
    var prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return function(){
      clearTimeout(t);
      document.removeEventListener("keydown", onKey, true);
      document.body.style.overflow = prevOverflow;
      var inv = invokerRef.current;
      if(inv && typeof inv.focus==="function"){ try{ inv.focus(); }catch(_){} }
    };
  },[open]);

  if(!open) return null;

  function onBackdropClick(e){
    if(e.target===e.currentTarget) props.onClose && props.onClose();
  }

  return E("div",{
    onClick:onBackdropClick,
    style:{position:"fixed",inset:0,background:"rgba(15,23,42,.55)",zIndex:1000,
      display:"flex",alignItems:"center",justifyContent:"center",padding:16}
  },
    E("div",{
      ref:dialogRef,
      role:"dialog","aria-modal":"true","aria-labelledby":labelId,
      tabIndex:-1,
      style:{background:P.card,borderRadius:16,maxWidth:maxW,width:"100%",
        maxHeight:"90vh",overflow:"hidden",display:"flex",flexDirection:"column",
        boxShadow:"0 24px 64px rgba(0,0,0,.32)",outline:"none"}
    },
      E("header",{style:{display:"flex",alignItems:"center",justifyContent:"space-between",
        padding:"16px 20px",borderBottom:"1px solid "+P.bdr,flexShrink:0}},
        E("h2",{id:labelId,style:{margin:0,fontSize:16,fontWeight:800,color:P.slate}},title),
        E("button",{type:"button","aria-label":"Close dialog",onClick:function(){props.onClose&&props.onClose()},
          style:{background:"transparent",border:"none",padding:8,borderRadius:8,cursor:"pointer",
            color:P.slateM,display:"flex",alignItems:"center",justifyContent:"center",minWidth:36,minHeight:36}},
          E(X,{size:18}))),
      E("div",{style:{padding:20,overflow:"auto",flex:1}}, props.children),
      props.footer ? E("footer",{style:{padding:"12px 20px",borderTop:"1px solid "+P.bdr,
        display:"flex",justifyContent:"flex-end",gap:8,flexShrink:0}}, props.footer) : null
    )
  );
}
